import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { QuestionData } from "../../entry/server/game.ts";
import { gameRepo } from "../../repo/game-repo.ts";
import { AppContext } from "../../context/app-context.tsx";
import AdminPageTitle from "../admin-pages/admin-page-title.tsx";
import { Pagination } from "../../lib/api.ts";
import PaginationView from "../../componrnts/pagination.tsx";
import { parseQuestionsXlsx } from "../../utils/parse-questions-xlsx.ts";

const inputClass =
  "w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 outline-none transition focus:border-indigo-400";

function correctOptionLabel(n: number) {
  return `گزینه ${n}`;
}

export default function QuestionsList() {
  const [questions, setQuestions] = useState<QuestionData[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [importing, setImporting] = useState(false);
  const [importTotal, setImportTotal] = useState(0);
  const [importDone, setImportDone] = useState(0);
  const [importFailed, setImportFailed] = useState(0);
  const [importParseSkipped, setImportParseSkipped] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { setMessage } = useContext(AppContext);

  const loadQuestions = useCallback(async () => {
    setLoading(true);
    try {
      const response = await gameRepo.getQuestions(page, searchQuery || undefined);
      if (!response.status || !response.data) {
        setMessage({ type: "error", message: response.message || "دریافت لیست سوالات ناموفق بود." });
        setQuestions([]);
        return;
      }
      setQuestions(response.data);
      setPagination(response.pagination ?? null);
    } catch {
      setMessage({ type: "error", message: "خطا در ارتباط با سرور. دوباره تلاش کنید." });
      setQuestions([]);
    } finally {
      setLoading(false);
    }
  }, [page, searchQuery, setMessage]);

  useEffect(() => {
    void loadQuestions();
  }, [loadQuestions]);

  const importPercent =
    importTotal > 0 ? Math.round(((importDone + importFailed) / importTotal) * 100) : 0;

  const onImportFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || importing) return;

    try {
      const { rows, errors } = await parseQuestionsXlsx(file);
      if (!rows.length) {
        setMessage({
          type: "error",
          message:
            errors.length > 0
              ? `هیچ ردیف معتبری یافت نشد. ${errors.length} ردیف نامعتبر.`
              : "هیچ ردیف معتبری در فایل یافت نشد.",
        });
        return;
      }

      setImportParseSkipped(errors.length);
      setImporting(true);
      setImportTotal(rows.length);
      setImportDone(0);
      setImportFailed(0);

      let done = 0;
      let failed = 0;

      for (const row of rows) {
        try {
          const response = await gameRepo.createQuestion(row);
          if (!response.status) {
            failed++;
          } else {
            done++;
          }
        } catch {
          failed++;
        }
        setImportDone(done);
        setImportFailed(failed);
      }

      const failPart = failed > 0 ? ` ${failed} مورد ناموفق بود.` : "";
      const skipPart = errors.length > 0 ? ` ${errors.length} ردیف در پارس رد شد.` : "";
      setMessage({
        type: failed > 0 && done === 0 ? "error" : "success",
        message: `${done} سوال با موفقیت وارد شد.${failPart}${skipPart}`,
      });

      await loadQuestions();
    } catch {
      setMessage({ type: "error", message: "خواندن فایل Excel ناموفق بود." });
    } finally {
      setImporting(false);
      setImportTotal(0);
      setImportDone(0);
      setImportFailed(0);
      setImportParseSkipped(0);
    }
  };

  const onSearchSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    setPage(1);
    setSearchQuery(searchInput.trim());
  };

  const onDelete = async (id: number) => {
    if (!window.confirm("این سوال حذف شود؟")) return;
    try {
      const response = await gameRepo.deleteQuestion(id);
      if (!response.status) {
        setMessage({ type: "error", message: response.message || "حذف سوال ناموفق بود." });
        return;
      }
      setQuestions((prev) => prev.filter((q) => q.id !== id));
      setMessage({ type: "success", message: "سوال با موفقیت حذف شد." });
    } catch {
      setMessage({ type: "error", message: "خطا در ارتباط با سرور. دوباره تلاش کنید." });
    }
  };

  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <AdminPageTitle title="سوالات بازی" />
        <div className="flex flex-wrap items-center gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls"
            className="hidden"
            disabled={importing}
            onChange={(e) => void onImportFile(e)}
          />
          <button
            type="button"
            disabled={importing}
            onClick={() => fileInputRef.current?.click()}
            className="rounded-xl border border-slate-700 px-4 py-2 text-sm font-medium text-slate-200 transition hover:border-indigo-500 disabled:cursor-not-allowed disabled:opacity-50"
          >
            ورود از Excel
          </button>
          <Link
            to="/admin/questions/new"
            className={`rounded-xl bg-indigo-500 px-4 py-2 text-sm font-medium text-white transition hover:bg-indigo-400 ${
              importing ? "pointer-events-none opacity-50" : ""
            }`}
          >
            ایجاد سوال
          </Link>
        </div>
      </div>

      {importing && importTotal > 0 ? (
        <div className="space-y-2 rounded-2xl border border-slate-800 bg-slate-900/60 p-4">
          <div className="flex flex-wrap items-center justify-between gap-2 text-sm text-slate-300">
            <span>در حال ورود سوالات...</span>
            <span>
              {importDone + importFailed} از {importTotal} — {importPercent}%
            </span>
          </div>
          <div className="h-2 overflow-hidden rounded-full bg-slate-800">
            <div
              className="h-full bg-indigo-500 transition-all duration-300"
              style={{ width: `${importPercent}%` }}
            />
          </div>
          {importParseSkipped > 0 ? (
            <p className="text-xs text-amber-300">{importParseSkipped} ردیف در پارس رد شد.</p>
          ) : null}
        </div>
      ) : null}

      <form onSubmit={onSearchSubmit} className="flex flex-col gap-2 sm:flex-row sm:items-end">
        <label className="flex-1 space-y-2">
          <span className="text-sm text-slate-300">جستجو</span>
          <input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="متن سوال..."
            className={inputClass}
            disabled={importing}
          />
        </label>
        <button
          type="submit"
          disabled={importing}
          className="rounded-lg bg-indigo-500 px-4 py-2 text-sm font-medium text-white transition hover:bg-indigo-400 disabled:cursor-not-allowed disabled:opacity-50"
        >
          جستجو
        </button>
      </form>

      {loading ? (
        <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-6 text-slate-300">
          در حال دریافت لیست سوالات...
        </div>
      ) : !questions.length ? (
        <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-6 text-slate-300">سوالی یافت نشد.</div>
      ) : (
        <>
          <div className="overflow-x-auto rounded-2xl border border-slate-800 bg-slate-900/60">
            <table className="min-w-full divide-y divide-slate-800 text-sm">
              <thead className="bg-slate-900/80">
                <tr>
                  <th className="px-4 py-3 text-right font-medium text-slate-300">شناسه</th>
                  <th className="px-4 py-3 text-right font-medium text-slate-300">سوال</th>
                  <th className="px-4 py-3 text-right font-medium text-slate-300">پاسخ صحیح</th>
                  <th className="px-4 py-3 text-right font-medium text-slate-300">عملیات</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {questions.map((q) => (
                  <tr key={q.id} className="transition hover:bg-slate-900">
                    <td className="whitespace-nowrap px-4 py-3 text-slate-300">{q.id}</td>
                    <td className="max-w-md truncate px-4 py-3 text-slate-100" title={q.question}>
                      {q.question || "—"}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-emerald-300">
                      {correctOptionLabel(q.correct_option)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-2">
                        <Link
                          to={`/admin/questions/${q.id}/edit`}
                          className={`inline-flex rounded-lg border border-indigo-700 px-3 py-1.5 text-xs text-indigo-200 transition hover:border-indigo-500 ${
                            importing ? "pointer-events-none opacity-50" : ""
                          }`}
                        >
                          ویرایش
                        </Link>
                        <button
                          type="button"
                          disabled={importing}
                          onClick={() => void onDelete(q.id)}
                          className="inline-flex rounded-lg border border-rose-800 px-3 py-1.5 text-xs text-rose-200 transition hover:border-rose-600 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          حذف
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {pagination ? <PaginationView pagination={pagination} onPageChange={setPage} /> : null}
        </>
      )}
    </section>
  );
}
